/*
PROV: COHINT030.BF.GA4_CONSENT_READER
REQ: cohorta-group-admin#30
WHY: Self-contained reader for the Cohorta consent-state contract
     (Think-Platinum/cohorta#439). This surface only needs to *read* consent to
     gate the GA4 loader — the banner UI itself is owned by #439. Until #439
     ships the canonical reader in @cohorta/shared, keep this file conformant
     with the contract doc (docs/consent-contract.md in the cohorta repo);
     mirrors the in-review website implementation (cohorta-website#14).
*/

export const CONSENT_STORAGE_KEY = "cohorta_consent";
export const CONSENT_CHANGED_EVENT = "cohorta:consent-changed";

/**
 * Policy version this surface understands. A stored record whose `version`
 * differs is treated as absent, which re-prompts the visitor. Bumped only
 * by a deliberate code change when the cookie policy materially changes.
 */
export const CONSENT_POLICY_VERSION = "1";

export type ConsentCategories = {
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
};

export type ConsentRecord = {
  version: string;
  consent_at: string;
  consent_source: "accept_all" | "reject_all" | "preference_centre";
  categories: ConsentCategories;
};

/**
 * Read the consent record for this origin. Returns null when no valid
 * record exists — absent, unparseable, wrong shape, or stale policy
 * version all read as "no consent" (fail closed).
 */
/**
 * Parse a raw stored value into a valid record, or null (absent, unparseable,
 * wrong shape, or stale policy version — fail closed). Pure, so a caller holding
 * the raw string (e.g. a useSyncExternalStore snapshot) can parse it without
 * re-reading storage.
 */
export function parseStoredConsent(raw: string | null): ConsentRecord | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ConsentRecord> | null;
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.version !== CONSENT_POLICY_VERSION) return null;
    if (!parsed.categories || typeof parsed.categories !== "object") return null;
    return parsed as ConsentRecord;
  } catch {
    return null;
  }
}

export function readConsentRecord(): ConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    return parseStoredConsent(window.localStorage.getItem(CONSENT_STORAGE_KEY));
  } catch {
    return null;
  }
}

/** True only when a current-version record explicitly grants analytics. */
export function hasAnalyticsConsent(): boolean {
  return readConsentRecord()?.categories?.analytics === true;
}

/**
 * Subscribe to consent changes dispatched by the #439 banner. Returns an
 * unsubscribe function. Listeners re-read storage rather than trusting the
 * event payload.
 */
export function onConsentChange(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(CONSENT_CHANGED_EVENT, listener);
  return () => window.removeEventListener(CONSENT_CHANGED_EVENT, listener);
}

// ─── Writer (the #439 consent banner) ─────────────────────────────────────────

export type ConsentSource = ConsentRecord["consent_source"];

export const GRANT_ALL: ConsentCategories = { functional: true, analytics: true, marketing: true };
export const GRANT_NONE: ConsentCategories = { functional: false, analytics: false, marketing: false };

function dispatchConsentChanged(record: ConsentRecord | null): void {
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: record }));
  } catch {
    // CustomEvent unavailable — the loader gate re-reads storage on its own triggers.
  }
}

/**
 * Persist a consent choice for this origin and notify listeners (the ADR-007
 * loader gate). Writes the versioned record synchronously, then fires
 * cohorta:consent-changed so same-page gates react without a reload. The record
 * contains no PII. Returns the record written.
 */
export function saveConsent(
  categories: ConsentCategories,
  consentSource: ConsentSource,
): ConsentRecord {
  const record: ConsentRecord = {
    version: CONSENT_POLICY_VERSION,
    consent_at: new Date().toISOString(),
    consent_source: consentSource,
    categories: { ...categories },
  };
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    } catch {
      // Storage full / disabled — the change event still fires for in-memory gates.
    }
    dispatchConsentChanged(record);
  }
  return record;
}

/** Revoke consent (return to first-visit state) and notify listeners. */
export function clearConsent(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // ignore
  }
  dispatchConsentChanged(null);
}
